import Image from 'next/image'
import { useRouter } from 'next/navigation'
import React, { useEffect, useState } from 'react'

import { AutoWithdrawalLabel } from '..'

import getFeatureFlag from '@/libs/actions/get-feature-flag'
import { specialTag } from '@/libs/constants/images-library'
import { minimumWithdrawWithoutAdminFee } from '@/libs/constants/minimum-withdraw'

type TRadioPayoutInfo = {
  readonly value: string
  readonly checked: boolean
  readonly image: string
  readonly altImage: string
  readonly provider: string
  readonly accountNumber?: string
  readonly onClick?: () => void
  readonly hideRadio?: boolean
  readonly showWithdrawalLabel?: boolean
  readonly isWithdrawalEnabled?: boolean
  readonly isSpecialAccount?: boolean
  readonly adminFee?: number
  readonly minimumWithdrawal?: number
  readonly disabled?: boolean
  readonly isFree?: boolean
  readonly reward: number
  readonly summaryMode?: boolean
  readonly active?: boolean
  readonly isBelowEarningLimit?: boolean
}

export default function RadioPayoutInfo({
  value,
  checked,
  image,
  altImage,
  provider,
  accountNumber,
  onClick,
  hideRadio,
  showWithdrawalLabel = false,
  isWithdrawalEnabled = false,
  isSpecialAccount = false,
  adminFee = 5000,
  minimumWithdrawal = 15000,
  disabled,
  isFree,
  reward,
  summaryMode = false,
  active,
  isBelowEarningLimit,
}: TRadioPayoutInfo) {
  const { push } = useRouter()
  const [gopayNoFeeFlag, setGopayNoFeeFlag] = useState<boolean>(false)

  useEffect(() => {
    ;(async () => {
      const featureFlag = await getFeatureFlag('gopayNoFee')
      setGopayNoFeeFlag(featureFlag)
    })()
  })

  return (
    <div className='relative flex flex-col gap-0'>
      <button
        type='button'
        className={`flex flex-col gap-2 border ${
          disabled || !active || isBelowEarningLimit
            ? '!cursor-not-allowed bg-neutral-grey-100'
            : 'bg-white'
        } px-4 py-3 ${
          checked && active && !isBelowEarningLimit ? 'border-pop-blue-700' : 'border-pop-lighter'
        } ${showWithdrawalLabel || !active ? 'rounded-t-lg' : 'rounded-lg'}
        ${!hideRadio && !disabled && 'cursor-pointer'}`}
        data-testid='radio-button'
        onClick={onClick}
        disabled={!active || disabled || isBelowEarningLimit}
      >
        <div className='flex w-full flex-row items-end justify-between gap-2'>
          <figure className='sentry-mask flex flex-row items-center gap-3'>
            <Image src={image} width={70} height={50} alt={altImage} className='h-8 w-11' />
            <figcaption className='flex flex-col gap-1 text-left'>
              <span className='text-xs font-bold text-pop-grey-800'>{provider}</span>
              {accountNumber && (
                <span className='text-xs font-normal text-pop-grey-600'>{accountNumber}</span>
              )}
            </figcaption>
          </figure>
          {!hideRadio && (
            <input
              type='radio'
              value={value}
              checked={checked}
              readOnly
              className={`ml-auto ${disabled ? 'cursor-default' : ''}`}
              disabled={disabled || !active || isBelowEarningLimit}
            />
          )}
        </div>

        {active && !isBelowEarningLimit && (
          <div
            className={`flex w-full flex-row items-center gap-1 rounded p-2 ${
              disabled ? 'bg-pop-grey-200' : 'bg-pop-blue-50'
            }`}
          >
            <p className={`text-xs ${disabled ? 'text-pop-grey-700' : 'text-pop-business-blue'}`}>
              Admin fee{' '}
              <span
                data-testID='admin-fee-amount'
                className={
                  isFree ||
                  reward >= minimumWithdrawWithoutAdminFee ||
                  (adminFee === 0 && gopayNoFeeFlag)
                    ? 'line-through'
                    : ''
                }
              >
                Rp{adminFee === 0 ? '5.000' : adminFee.toLocaleString('de-DE')}
              </span>
            </p>
            {(isFree || reward >= minimumWithdrawWithoutAdminFee || adminFee === 0) && (
              <span className='rounded-[36px] border border-pop-green-200 bg-pop-succees-background px-2 py-[2px] text-xs text-pop-green-600'>
                Free
              </span>
            )}
          </div>
        )}
        {!summaryMode && (
          <p className='text-left text-xs text-pop-grey-600'>
            {minimumWithdrawal === 0
              ? 'No minimum withdrawal'
              : `Minimum withdrawal Rp${minimumWithdrawal.toLocaleString('de-DE')}`}
          </p>
        )}
      </button>
      {provider?.toLowerCase() === 'gopay' && !summaryMode && gopayNoFeeFlag && active && (
        <AutoWithdrawalLabel
          isWithdrawalEnabled={isWithdrawalEnabled}
          onClick={() => push('/profile')}
        />
      )}
      {isSpecialAccount && !summaryMode && gopayNoFeeFlag && active && !isBelowEarningLimit && (
        <span className='absolute right-[-12px] top-0'>
          <Image
            src={specialTag.src}
            alt={specialTag.alt}
            width={59}
            height={33}
            className='h-[33] w-[59]'
            data-testid='special-tag'
          />
        </span>
      )}
      {(isBelowEarningLimit || !active) && (
        <div className='flex w-full items-center rounded-b-lg bg-neutral-grey-700 px-4 py-3'>
          <span className='text-left text-xs-regular font-normal text-neutral-grey-050'>
            {!active
              ? 'This withdrawal method is currently unavailable.'
              : 'Your earnings are not sufficient to withdraw using this method.'}
          </span>
        </div>
      )}
    </div>
  )
}
